import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BiService from '@app/model/bi/service/BiService';
import { BiBookingCut, BiBookingReport, BiColumn, BiColumns, BiReport, ChannelGroups, compsetParts, DailyCompset, EvolutionGrain, GuestMixSplit, joinCompset, PickupAlertSetting, PickupPeriod, PropertyGroups, RevenueScope, RoomTypeBasis, runQueue, SegmentationView, SegmentGroups, StlyBasis, VatBasis } from '@app/model/bi/Bi';

// The BI data changes only after a reservations import (a scheduled Change Check), so a page change or a view seen
// before reads the cache; a save of the groups invalidates the report and the evolution at once.
const BI_STALE_TIME = 5 * 60 * 1000;

export const BI_KEYS = {
	scope: ['bi', 'scope'] as const,
	segmentation: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, view: SegmentationView, includeOptions: boolean) =>
		['bi', 'segmentation', companyHotelId, propertyGroupId, stayFrom, stayTo, view, includeOptions] as const,
	evolution: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, grain: EvolutionGrain, view: SegmentationView, includeOptions: boolean) =>
		['bi', 'evolution', companyHotelId, propertyGroupId, stayFrom, stayTo, grain, view, includeOptions] as const,
	groups: ['bi', 'groups'] as const,
	roomTypes: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, basis: RoomTypeBasis, includeOptions: boolean) =>
		['bi', 'roomTypes', companyHotelId, propertyGroupId, stayFrom, stayTo, basis, includeOptions] as const,
	ratePlans: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, includeOptions: boolean) =>
		['bi', 'ratePlans', companyHotelId, propertyGroupId, stayFrom, stayTo, includeOptions] as const,
	ratePlanEvolution: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, windowDays: number,
		revenueScope: RevenueScope, vat: VatBasis, includeOptions: boolean) =>
		['bi', 'ratePlanEvolution', companyHotelId, propertyGroupId, stayFrom, stayTo, windowDays, revenueScope, vat, includeOptions] as const,
	guestMix: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, split: GuestMixSplit, includeOptions: boolean) =>
		['bi', 'guestMix', companyHotelId, propertyGroupId, stayFrom, stayTo, split, includeOptions] as const,
	guestMixEvolution: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, split: GuestMixSplit,
		windowDays: number, includeOptions: boolean) => ['bi', 'guestMixEvolution', companyHotelId, propertyGroupId, stayFrom, stayTo, split, windowDays, includeOptions] as const,
	propertyGroups: ['bi', 'propertyGroups'] as const,
	bookings: (report: BiBookingReport, companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string,
		cut: BiBookingCut, keys: string[], name: string | null, includeOptions: boolean) => ['bi', 'bookings', report, companyHotelId, propertyGroupId, stayFrom, stayTo, cut, keys, name, includeOptions] as const,
	channels: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, includeOptions: boolean) =>
		['bi', 'channels', companyHotelId, propertyGroupId, stayFrom, stayTo, includeOptions] as const,
	channelEvolution: (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, windowDays: number,
		revenueScope: RevenueScope, vat: VatBasis, includeOptions: boolean) =>
		['bi', 'channelEvolution', companyHotelId, propertyGroupId, stayFrom, stayTo, windowDays, revenueScope, vat, includeOptions] as const,
	channelGroups: ['bi', 'channelGroups'] as const,
	columns: (report: BiReport) => ['bi', 'columns', report] as const,
	daily: (companyHotelId: number, stayFrom: string, stayTo: string, stlyBasis: StlyBasis, includeOptions: boolean) => ['bi', 'daily', companyHotelId, stayFrom, stayTo, stlyBasis, includeOptions] as const,
	dailyNew: (companyHotelId: number, stayFrom: string, stayTo: string, includeOptions: boolean) => ['bi', 'dailyNew', companyHotelId, stayFrom, stayTo, includeOptions] as const,
	pickup: (companyHotelId: number | null, propertyGroupId: number | null, period: PickupPeriod, bookFrom: string | null, bookTo: string | null,
		includeOptions: boolean) => ['bi', 'pickup', companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions] as const,
	pickupBookings: (companyHotelId: number | null, propertyGroupId: number | null, period: PickupPeriod, bookFrom: string | null, bookTo: string | null,
		includeOptions: boolean) => ['bi', 'pickupBookings', companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions] as const,
	pickupAlertSetting: ['bi', 'pickupAlertSetting'] as const,
	dailyCompset: (companyHotelId: number, from: string, to: string) => ['bi', 'dailyCompset', companyHotelId, from, to] as const,
	dailyDayPopup: (companyHotelId: number, night: string, choice: DailyCompset['comparisonMethod']) => ['bi', 'dailyDayPopup', companyHotelId, night, choice] as const,
};

// enabled: false asks nothing (useBiAccess, when the user can have no BI scope); a scope already known is still returned.
export const useBiScope = (enabled = true) => useQuery({
	queryKey: BI_KEYS.scope,
	queryFn: BiService.findBiScope,
	staleTime: BI_STALE_TIME,
	enabled,
});

// enabled: false until the scope and the groups are known, so that no call goes out with an empty period or the wrong view.
export const useSegmentationReport = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, view: SegmentationView, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.segmentation(companyHotelId, propertyGroupId, stayFrom, stayTo, view, includeOptions),
	queryFn: () => BiService.findSegmentationReport(companyHotelId, propertyGroupId, stayFrom, stayTo, view, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// enabled = false until the page knows its selection (a ?group= link waits for the Property groups).
export const useSegmentEvolution = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, grain: EvolutionGrain, view: SegmentationView,
	includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.evolution(companyHotelId, propertyGroupId, stayFrom, stayTo, grain, view, includeOptions),
	queryFn: () => BiService.findSegmentEvolution(companyHotelId, propertyGroupId, stayFrom, stayTo, grain, view, includeOptions),
	enabled,
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
});

export const useSegmentGroups = () => useQuery({
	queryKey: BI_KEYS.groups,
	queryFn: BiService.findSegmentGroups,
	staleTime: BI_STALE_TIME,
});

// A save changes every report of the company: the groups apply to all its properties.
export const useSaveSegmentGroups = (onSaved: (groups: SegmentGroups) => void, onError: (error: string) => void) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: BiService.saveSegmentGroups,
		onSuccess: groups => {
			queryClient.setQueryData(BI_KEYS.groups, groups);
			void queryClient.invalidateQueries({ queryKey: ['bi', 'segmentation'] });
			void queryClient.invalidateQueries({ queryKey: ['bi', 'evolution'] });
			onSaved(groups);
		},
		onError,
	});
};

export const useRoomTypeReport = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, basis: RoomTypeBasis, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.roomTypes(companyHotelId, propertyGroupId, stayFrom, stayTo, basis, includeOptions),
	queryFn: () => BiService.findRoomTypeReport(companyHotelId, propertyGroupId, stayFrom, stayTo, basis, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// The Rate Plans report (BI report 3, ROO2-263). The grouping and the revenue switches change only the screen, not the query.
export const useRatePlanReport = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.ratePlans(companyHotelId, propertyGroupId, stayFrom, stayTo, includeOptions),
	queryFn: () => BiService.findRatePlanReport(companyHotelId, propertyGroupId, stayFrom, stayTo, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// The build-up of the Rate Plans report (ROO2-267) over the window (30, 90, 180 or 365 days). The cache of the report (T13).
export const useRatePlanEvolution = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string,
	windowDays: number, revenueScope: RevenueScope, vat: VatBasis, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.ratePlanEvolution(companyHotelId, propertyGroupId, stayFrom, stayTo, windowDays, revenueScope, vat, includeOptions),
	queryFn: () => BiService.findRatePlanEvolution(companyHotelId, propertyGroupId, stayFrom, stayTo, windowDays, revenueScope, vat, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// The Guest Mix report (BI report 4, ROO2-264). The revenue switches change only the screen, not the query.
export const useGuestMixReport = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, split: GuestMixSplit, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.guestMix(companyHotelId, propertyGroupId, stayFrom, stayTo, split, includeOptions),
	queryFn: () => BiService.findGuestMixReport(companyHotelId, propertyGroupId, stayFrom, stayTo, split, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// The Property groups of the company (BI Property groups): the picker of every BI page reads them.
export const usePropertyGroups = () => useQuery({
	queryKey: BI_KEYS.propertyGroups,
	queryFn: BiService.findPropertyGroups,
	staleTime: BI_STALE_TIME,
});

// A save or a delete changes the properties of a group: the reports of that group read the new list. Each answer holds
// all groups of the company, so the picker and the editor show the new list at once.
const usePropertyGroupMutation = <T,>(mutationFn: (input: T) => Promise<PropertyGroups>, onDone: (groups: PropertyGroups) => void, onError: (error: string) => void) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: groups => {
			queryClient.setQueryData(BI_KEYS.propertyGroups, groups);
			['segmentation', 'evolution', 'roomTypes', 'ratePlans', 'guestMix', 'guestMixEvolution', 'channels', 'channelEvolution'].forEach(report => void queryClient.invalidateQueries({ queryKey: ['bi', report] }));
			onDone(groups);
		},
		onError,
	});
};

export const useSavePropertyGroup = (onSaved: (groups: PropertyGroups) => void, onError: (error: string) => void) =>
	usePropertyGroupMutation(BiService.savePropertyGroup, onSaved, onError);

export const useDeletePropertyGroup = (onDeleted: (groups: PropertyGroups) => void, onError: (error: string) => void) =>
	usePropertyGroupMutation(BiService.deletePropertyGroup, onDeleted, onError);

// ROO2-265: the evolution of the period over the window (30, 90, 180 or 365 days).
export const useGuestMixEvolution = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, split: GuestMixSplit,
	windowDays: number, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.guestMixEvolution(companyHotelId, propertyGroupId, stayFrom, stayTo, split, windowDays, includeOptions),
	queryFn: () => BiService.findGuestMixEvolution(companyHotelId, propertyGroupId, stayFrom, stayTo, split, windowDays, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// ROO2-283: the Columns choice of the user for a BI report. It changes only by the user's own saves, so it is read once.
export const useBiColumns = (report: BiReport) => useQuery({
	queryKey: BI_KEYS.columns(report),
	queryFn: () => BiService.findBiColumns(report),
	staleTime: Infinity,
});

export const useSaveBiColumns = (report: BiReport) => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (columns: BiColumn[]) => BiService.saveBiColumns(report, columns),
		// The table takes the change at once; a failed save keeps it for this visit (the page says so).
		onMutate: columns => { client.setQueryData<BiColumns>(BI_KEYS.columns(report), { report, columns }); },
		onSuccess: saved => client.setQueryData(BI_KEYS.columns(report), saved),
	});
};

// The Channels report (BI report 5). The groups and the revenue switches change only the screen, not the query (RQ-04: a
// group applies to all dates, so the report sends the raw names and the page puts them into the groups).
export const useChannelReport = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.channels(companyHotelId, propertyGroupId, stayFrom, stayTo, includeOptions),
	queryFn: () => BiService.findChannelReport(companyHotelId, propertyGroupId, stayFrom, stayTo, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

export const useChannelEvolution = (companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string,
	windowDays: number, revenueScope: RevenueScope, vat: VatBasis, includeOptions: boolean, enabled = true) => useQuery({
	queryKey: BI_KEYS.channelEvolution(companyHotelId, propertyGroupId, stayFrom, stayTo, windowDays, revenueScope, vat, includeOptions),
	queryFn: () => BiService.findChannelEvolution(companyHotelId, propertyGroupId, stayFrom, stayTo, windowDays, revenueScope, vat, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

export const useChannelGroups = () => useQuery({
	queryKey: BI_KEYS.channelGroups,
	queryFn: BiService.findChannelGroups,
	staleTime: BI_STALE_TIME,
});

// RQ-05: a save applies at once. The report sends raw names, so only the groups change: the page groups them again.
export const useSaveChannelGroups = (onSaved: (groups: ChannelGroups) => void, onError: (error: string) => void) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: BiService.saveChannelGroups,
		onSuccess: groups => {
			queryClient.setQueryData(BI_KEYS.channelGroups, groups);
			onSaved(groups);
		},
		onError,
	});
};
// ROO2-288: the Daily Performance report of one property; nothing is asked until the page knows the property.
export const useDailyPerformance = (companyHotelId: number | null, stayFrom: string, stayTo: string, stlyBasis: StlyBasis, includeOptions: boolean) => useQuery({
	queryKey: BI_KEYS.daily(companyHotelId ?? 0, stayFrom, stayTo, stlyBasis, includeOptions),
	queryFn: () => BiService.findDailyPerformance(companyHotelId as number, stayFrom, stayTo, stlyBasis, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled: companyHotelId !== null,
});

// ROO2-314 Part 3: the New rows of every PU block of the window, read only while a New RN or New ADR column is shown (enabled).
export const useDailyNewNights = (companyHotelId: number | null, stayFrom: string, stayTo: string, includeOptions: boolean, enabled: boolean) => useQuery({
	queryKey: BI_KEYS.dailyNew(companyHotelId ?? 0, stayFrom, stayTo, includeOptions),
	queryFn: () => BiService.findDailyNewNights(companyHotelId as number, stayFrom, stayTo, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled: enabled && companyHotelId !== null,
});

// ROO2-289 B5: the day popup of a night, read when its card opens (the hook mounts with the card). ROO2-301 U1: with the
// line of the selected compset value (choice) only.
const dayPopupQuery = (companyHotelId: number, night: string, choice: DailyCompset['comparisonMethod']) => ({
	queryKey: BI_KEYS.dailyDayPopup(companyHotelId, night, choice),
	queryFn: () => BiService.findDailyDayPopup(companyHotelId, night, choice),
	staleTime: BI_STALE_TIME,
});
export const useDailyDayPopup = (companyHotelId: number, night: string, choice: DailyCompset['comparisonMethod']) =>
	useQuery(dayPopupQuery(companyHotelId, night, choice));

// ROO2-301 W2, U2, U4: the day popups of the nights (in order, the nearest first) load in the background, at most 31 nights
// and 2 at a time, so a chip opens its popup at once. A change of property, nights or choice stops the loads that did not
// start. preload(night) loads one night at once (U3: a pointer on its row). A night in the cache or on its way is not asked again.
const DAY_POPUP_PRELOAD_NIGHTS = 31;
const DAY_POPUP_PRELOAD_AT_ONCE = 2;
export const usePreloadDailyDayPopups = (companyHotelId: number | null, nights: string[], choice: DailyCompset['comparisonMethod'], enabled: boolean) => {
	const queryClient = useQueryClient();
	const list = nights.slice(0, DAY_POPUP_PRELOAD_NIGHTS).join(',');
	useEffect(() => {
		if (!enabled || companyHotelId === null || list === '') return undefined;
		let stopped = false;
		void runQueue(list.split(','), DAY_POPUP_PRELOAD_AT_ONCE, night => queryClient.prefetchQuery(dayPopupQuery(companyHotelId, night, choice)), () => stopped);
		return () => { stopped = true; };
	}, [queryClient, companyHotelId, list, choice, enabled]);
	return useCallback((night: string) => {
		if (enabled && companyHotelId !== null) void queryClient.prefetchQuery(dayPopupQuery(companyHotelId, night, choice));
	}, [queryClient, companyHotelId, choice, enabled]);
};

// ROO2-289: the compset of each night of the window. A failure leaves the chips empty ("–"); the night figures still show.
// ROO2-298: a range over 6 months comes in two halves at the same time (compsetParts).
export const useDailyCompset = (companyHotelId: number | null, from: string, to: string) => useQuery({
	queryKey: BI_KEYS.dailyCompset(companyHotelId ?? 0, from, to),
	queryFn: () => Promise.all(compsetParts(from, to).map(p => BiService.findDailyCompset(companyHotelId as number, p.from, p.to))).then(joinCompset),
	staleTime: BI_STALE_TIME,
	enabled: companyHotelId !== null,
});

// ROO2-284: the bookings behind a number; off until the drawer asks (request null).
export const useBiBookings = (request: { report: BiBookingReport, companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string,
	stayTo: string, cut: BiBookingCut, keys: string[], name: string | null, includeOptions: boolean } | null) => useQuery({
	queryKey: request === null ? ['bi', 'bookings', 'none'] as const
		: BI_KEYS.bookings(request.report, request.companyHotelId, request.propertyGroupId, request.stayFrom, request.stayTo, request.cut, request.keys, request.name, request.includeOptions),
	queryFn: () => request === null ? Promise.reject(new Error('no request'))
		: BiService.findBiBookings(request.report, request.companyHotelId, request.propertyGroupId, request.stayFrom, request.stayTo, request.cut, request.keys, request.name, request.includeOptions),
	staleTime: BI_STALE_TIME,
	enabled: request !== null,
});

// ROO2-314 Part 2: Pick-up monitoring of a scope and a booked period. The figures of the last choice stay while a new one
// loads (the page shows them dimmed); enabled false (a custom period the server would refuse) asks nothing.
export const usePickupMonitoring = (companyHotelId: number | null, propertyGroupId: number | null, period: PickupPeriod, bookFrom: string | null,
	bookTo: string | null, includeOptions: boolean, enabled: boolean) => useQuery({
	queryKey: BI_KEYS.pickup(companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions),
	queryFn: () => BiService.findPickupMonitoring(companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// ROO2-314 Part 2 (B2, B3): the bookings of the period, loaded apart from the figures (the first cards do not wait for it).
export const usePickupBookings = (companyHotelId: number | null, propertyGroupId: number | null, period: PickupPeriod, bookFrom: string | null,
	bookTo: string | null, includeOptions: boolean, enabled: boolean) => useQuery({
	queryKey: BI_KEYS.pickupBookings(companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions),
	queryFn: () => BiService.findPickupBookings(companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions),
	placeholderData: previous => previous,
	staleTime: BI_STALE_TIME,
	enabled,
});

// ROO2-314 D9: the ADR alert setting of the user. It changes only by the user's own saves, so it is read once.
export const usePickupAlertSetting = () => useQuery({
	queryKey: BI_KEYS.pickupAlertSetting,
	queryFn: () => BiService.findPickupAlertSetting(),
	staleTime: Infinity,
});

export const useSavePickupAlertSetting = () => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (setting: PickupAlertSetting) => BiService.savePickupAlertSetting(setting),
		onSuccess: saved => client.setQueryData(BI_KEYS.pickupAlertSetting, saved),
	});
};
